/**
 * ua-detect.ts — F1d UA-based device/browser detection.
 *
 * Pure module: takes a UA string, returns structured OS/browser fields.
 * No React, no DOM, no window imports — safe for SSR and unit tests.
 *
 * UA_SIGNATURES = live config object (§F1:156):
 *   - NOT hardcoded if-literals in logic
 *   - Centralised so in-app heuristics can be updated without a code change
 *   - Exported for tests and potential runtime patching
 */

export type BrowserId =
  | 'safari'
  | 'chrome'
  | 'instagram'
  | 'facebook'
  | 'tiktok'
  | 'other';

export type DeviceOs = 'ios' | 'android' | 'other';

export interface UaDetectResult {
  browser: BrowserId;
  inApp: boolean;
}

/**
 * UA_SIGNATURES — live config registry for in-app and browser detection.
 *
 * In-app signals are fragile (social apps change UAs on every release).
 * Centralising them here allows updates without touching detection logic.
 *
 * Signatures are tested via substring-match (UA.includes(sig)).
 * Order within each array does not matter.
 */
export const UA_SIGNATURES = {
  /** In-app WebView fingerprints. Checked BEFORE regular browser detection. */
  inApp: {
    instagram: ['Instagram'],
    facebook: ['FBAN', 'FBAV'],
    tiktok: ['BytedanceWebview', 'musical_ly', 'TikTok'],
  },
  /** Regular (non-in-app) browser substrings. */
  browser: {
    /** CriOS = Chrome for iOS (also has Safari in UA) */
    chrome: ['Chrome', 'CriOS'],
    safari: ['Safari'],
  },
} as const;

type InAppKey = keyof typeof UA_SIGNATURES.inApp;

/**
 * detectOs — OS detection from UA string.
 *
 * Checks for iOS device markers before Android so iPads/iPhones
 * with "Android" emulation in UA string are classified correctly.
 *
 * @returns 'ios' | 'android' | 'other'
 */
export function detectOs(ua: string): DeviceOs {
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

/**
 * detectBrowser — browser + in-app WebView classification from UA string.
 *
 * Priority order:
 *   1. In-app detection (Instagram/Facebook/TikTok UAs also contain Safari/Chrome)
 *   2. Chrome / CriOS
 *   3. Safari (no Chrome/CriOS in UA)
 *   4. other
 *
 * @returns UaDetectResult { browser, inApp }
 */
export function detectBrowser(ua: string): UaDetectResult {
  // 1. In-app detection — highest priority
  for (const [key, sigs] of Object.entries(UA_SIGNATURES.inApp) as Array<
    [InAppKey, readonly string[]]
  >) {
    if (sigs.some((sig) => ua.includes(sig))) {
      return { browser: key, inApp: true };
    }
  }

  // 2. Chrome / CriOS (must check before Safari — Chrome on iOS has both)
  if (
    UA_SIGNATURES.browser.chrome.some((sig) => ua.includes(sig))
  ) {
    return { browser: 'chrome', inApp: false };
  }

  // 3. Safari (no Chrome / CriOS present)
  if (ua.includes('Safari')) {
    return { browser: 'safari', inApp: false };
  }

  return { browser: 'other', inApp: false };
}
